import { useState } from 'react'
import { Outlet, Navigate } from 'react-router-dom'
import { Menu } from 'lucide-react'
import { api, ApiError } from '@/lib/api'
import { Sidebar } from '@/components/Sidebar'
import { FuelioIcon } from '@/components/FuelioIcon'
import { useI18n, type TKey } from '@/lib/i18n'
import { isSignedOut } from '@/lib/session'
import { useStaleCache } from '@/lib/cache'

export function Layout() {
  const { t } = useI18n()
  const [errorKey, setErrorKey] = useState<TKey | null>(null)
  const [unauthorized, setUnauthorized] = useState(false)
  // Mobile drawer 开关。desktop (≥ md) 用 CSS 永远显示 sidebar，这个状态被忽略。
  const [sidebarOpen, setSidebarOpen] = useState(false)

  // Honor the client-side "signed out" flag set by the sign-out button.
  // Without this gate, DEV_ADMIN_BYPASS in the API would silently re-admit
  // the user on every page load — the "logout" wouldn't actually keep them
  // out. In prod this is also a useful belt-and-suspenders: if the session
  // cookie is somehow still present after sign-out, the SPA still routes
  // them through the login page.
  const signedOut = isSignedOut()

  // Stale-while-revalidate：从 sessionStorage 读上次的 whoami 立刻渲染
  // sidebar，同时后台 revalidate。同一会话内切路由 / 刷新都不再闪 "正在加载"。
  const { data: who } = useStaleCache(
    'admin.whoami.v1',
    () => api.whoami().then((r) => r.admin),
    {
      skip: signedOut,
      onError: (err) => {
        const e = err as ApiError
        if (e?.status === 401) setUnauthorized(true)
        else if (e?.status === 500) setErrorKey('layout.error.misconfig')
        else setErrorKey('layout.error.network')
      },
    },
  )
  const adminEmail = who?.email
  const adminVia = who?.via

  if (signedOut) {
    return <Navigate to="/login?signed-out=1" replace />
  }

  if (unauthorized) {
    return <Navigate to="/login" replace />
  }

  if (errorKey) {
    return (
      <div className="flex h-full items-center justify-center p-8">
        <div className="max-w-md rounded-lg border border-danger/40 bg-danger/10 p-6 text-sm">
          <div className="font-semibold text-danger mb-1">{t('layout.error.title')}</div>
          <div className="text-muted-foreground">{t(errorKey)}</div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-full">
      <Sidebar
        adminEmail={adminEmail}
        adminVia={adminVia}
        mobileOpen={sidebarOpen}
        onMobileClose={() => setSidebarOpen(false)}
      />
      <div className="flex-1 flex flex-col min-w-0">
        {/* mobile-only top bar：汉堡按钮唤出 drawer + Fuelio 标识。
            md+ 上隐藏，sidebar 自己常驻。 */}
        <header className="md:hidden flex items-center gap-3 px-3 h-12 border-b border-border bg-card sticky top-0 z-20">
          <button
            type="button"
            onClick={() => setSidebarOpen(true)}
            aria-label={t('app.action.openMenu')}
            className="p-2 -ml-2 rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
          >
            <Menu size={20} strokeWidth={2}/>
          </button>
          <div className="flex items-center gap-2">
            <FuelioIcon size={24} className="rounded shadow-sm"/>
            <span className="text-sm font-semibold tracking-tight">Fuelio</span>
            <span className="text-[10px] text-muted-foreground uppercase tracking-wider">{t('app.brand.role')}</span>
          </div>
        </header>
        <main className="flex-1 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
